import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { storiesApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import BlockEditor from '../components/BlockEditor';
import SEO from '../components/SEO';

const TYPES = [
  { value: 'personal',      label: '✍️ Personal Story' },
  { value: 'investigation', label: '🔍 Investigation' },
  { value: 'talent',        label: '⭐ Talent Spotlight' },
  { value: 'welfare',       label: '🤝 Welfare & Advocacy' },
  { value: 'interview',     label: '🎙️ Interview' },
];

function autoSlug(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function Write() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const editSlug = searchParams.get('edit');

  const [form, setForm] = useState({
    title: '', slug: '', story_type: 'personal', excerpt: '', tags: '',
  });
  const [blocks, setBlocks] = useState([{ id: 'b0', type: 'paragraph', content: '' }]);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const coverRef = useRef(null);

  // Load story for editing
  useEffect(() => {
    if (!editSlug) return;
    setLoading(true);
    storiesApi.detail(editSlug).then(r => {
      const s = r.data;
      setForm({
        title: s.title,
        slug: s.slug,
        story_type: s.story_type,
        excerpt: s.excerpt,
        tags: (s.tags || []).map(t => t.name).join(', '),
      });
      if (s.blocks && s.blocks.length > 0) {
        setBlocks(s.blocks.map((b, i) => ({ ...b, id: b.id || `b${i}` })));
      } else if (s.content) {
        setBlocks([{ id: 'b0', type: 'paragraph', content: s.content }]);
      }
      if (s.cover_image_url) setCoverPreview(s.cover_image_url);
    }).catch(() => setError('Story not found.')).finally(() => setLoading(false));
  }, [editSlug]);

  if (!user) {
    return (
      <div style={{ padding: '4rem 0', textAlign: 'center' }}>
        <div className="container">
          <h2>Sign in to write a story</h2>
          <a href="/login" className="btn btn-primary" style={{ marginTop: '1rem' }}>Sign in</a>
        </div>
      </div>
    );
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCoverChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (status) => {
    setError('');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('slug', form.slug || autoSlug(form.title));
      fd.append('story_type', form.story_type);
      fd.append('excerpt', form.excerpt);
      fd.append('status', status);
      fd.append('blocks', JSON.stringify(blocks));
      if (coverFile) fd.append('cover_image', coverFile);
      // Parse tags
      const tagList = form.tags.split(',').map(t => t.trim()).filter(Boolean);
      tagList.forEach(t => fd.append('tags', t));

      let res;
      if (editSlug) {
        res = await storiesApi.update(editSlug, fd);
      } else {
        res = await storiesApi.create(fd);
      }
      if (status === 'published') {
        navigate(`/stories/${res.data.slug}`);
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData) {
        setError(Object.values(errData).flat().join(' '));
      } else {
        setError('Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-text">Loading story…</div>;

  return (
    <div style={{ background: 'var(--bg-card)', minHeight: '100vh' }}>
      <SEO
        title={editSlug ? `Editing: ${form.title || 'Story'}` : 'Write a Story'}
        description="Share your African football story on Pitch & Pen."
        noindex
      />
      {/* Top bar */}
      <div style={{
        borderBottom: '1px solid var(--border)', padding: '0.75rem 0',
        position: 'sticky', top: 'var(--nav-h)', background: 'white', zIndex: 50,
      }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: '0.8rem', color: 'var(--text-light)' }}>
            {editSlug ? 'Editing story' : 'New story'}
            {saved && <span style={{ marginLeft: '0.75rem', color: 'var(--green)' }}>✓ Saved</span>}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => handleSubmit('draft')} disabled={saving} className="btn btn-ghost btn-sm">
              {saving ? 'Saving…' : 'Save draft'}
            </button>
            <button onClick={() => handleSubmit('published')} disabled={saving || !form.title.trim()} className="btn btn-primary btn-sm">
              {saving ? 'Publishing…' : editSlug ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      <div className="container" style={{ maxWidth: 860, paddingTop: '2.5rem', paddingBottom: '4rem' }}>
        {error && <div className="form-error">{error}</div>}

        {/* Story type selector */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
          {TYPES.map(t => (
            <button key={t.value} onClick={() => set('story_type', t.value)}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: 20, border: '1.5px solid',
                fontFamily: 'var(--font-sans)', fontSize: '0.82rem', fontWeight: 500,
                cursor: 'pointer', transition: 'all 0.12s',
                background: form.story_type === t.value ? 'var(--green)' : 'transparent',
                color: form.story_type === t.value ? 'white' : 'var(--text-muted)',
                borderColor: form.story_type === t.value ? 'var(--green)' : 'var(--border)',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Cover image */}
        <div style={{ marginBottom: '2rem' }}>
          {coverPreview ? (
            <div style={{ position: 'relative' }}>
              <img src={coverPreview} alt="Cover" style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: 'var(--radius)', display: 'block' }} />
              <div style={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 8 }}>
                <button className="btn btn-sm" style={{ background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none' }}
                  onClick={() => coverRef.current?.click()}>
                  Change cover
                </button>
                <button className="btn btn-sm" style={{ background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none' }}
                  onClick={() => { setCoverPreview(null); setCoverFile(null); }}>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => coverRef.current?.click()}
              style={{
                width: '100%', padding: '2rem', border: '2px dashed var(--border-mid)',
                borderRadius: 'var(--radius)', background: 'var(--bg)', cursor: 'pointer',
                fontFamily: 'var(--font-sans)', fontSize: '0.875rem', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.background = 'var(--green-light)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-mid)'; e.currentTarget.style.background = 'var(--bg)'; }}
            >
              🖼 Add cover image
            </button>
          )}
          <input ref={coverRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverChange} />
        </div>

        {/* Title */}
        <textarea
          value={form.title}
          onChange={e => { set('title', e.target.value); if (!editSlug) set('slug', autoSlug(e.target.value)); }}
          placeholder="Your story headline…"
          rows={2}
          style={{
            width: '100%', border: 'none', outline: 'none', resize: 'none',
            fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
            fontWeight: 700, lineHeight: 1.2, color: 'var(--text)',
            background: 'transparent', marginBottom: '1.25rem',
          }}
          onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
        />

        {/* Excerpt */}
        <textarea
          value={form.excerpt}
          onChange={e => set('excerpt', e.target.value)}
          placeholder="Short excerpt — shown in listings and previews…"
          rows={2}
          style={{
            width: '100%', border: 'none', outline: 'none', resize: 'none',
            fontFamily: 'var(--font-serif)', fontSize: '1.1rem', fontStyle: 'italic',
            lineHeight: 1.65, color: 'var(--text-muted)', background: 'transparent',
            borderBottom: '1px solid var(--border)', paddingBottom: '1.25rem', marginBottom: '1.5rem',
          }}
          onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
        />

        {/* Block editor */}
        <BlockEditor blocks={blocks} onChange={setBlocks} />

        {/* Metadata footer */}
        <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--border)' }}>
          <h4 style={{ fontFamily: 'var(--font-sans)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Story settings
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">URL slug</label>
              <input className="form-input" value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="url-slug" />
            </div>
            <div className="form-group">
              <label className="form-label">Tags (comma-separated)</label>
              <input className="form-input" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="africa, football, talent…" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
